// Station side of handing orders over (M7, WP7.3; plan §9.1, §9.3; D-024): the orders this
// station handed over and what the server answered.
//
// A hand-over is one `order.handover` command in the outbox; in the same transaction a
// local-only `handover_action` row records it (`pending`), with what the orders view needs to
// show it (the order's number, customer, promised time and lines). The uploader writes the
// server's final answer onto the row (`applied`, `applied_with_flags`, `held`, `rejected`,
// with the answer's data and flags), so the view never calls an order handed over "in the
// office" before the server says so.
//
// Exactly once from the station too: an order handed over in full here can't be handed over
// again here — not even when the office didn't record it: the goods left the counter, so a
// manager must check it, never a second hand-over (D-076, UI7S-2). A part hand-over names the
// quantity per line that went out (D-076, UI7S-3) and can be followed by another part or by
// the rest.
//
// The row keeps the customer's name (its `detail`), so it doesn't outlive its day: once the
// business day moves on, answered rows of earlier days go (review CR7-9; POPIA-minimal, as the
// `orders_handover` stream drops the day's orders). Rows still waiting for the server or held
// for a manager stay until they are answered or resolved.

import type { CommandResponse } from '@enp/domain';
import type { LocalEffect, LocalReader, LocalTx } from './engine.js';
import type { HeldResolution } from './held-actions.js';

/**
 * Where a hand-over stands: `pending` until the server's final answer; a held one becomes
 * `booked` or `voided` once a manager resolves it (CR8B-4, ./held-actions.ts).
 */
export type HandoverActionStatus =
  'pending' | 'applied' | 'applied_with_flags' | 'held' | 'rejected' | 'booked' | 'voided';

/** The order as the station knew it when it was handed over. */
export interface HandoverDetail {
  number: string;
  customer_name: string | null;
  promised_at: string | null;
  lines: { id: string; name: string; qty: string; unit: string | null }[];
  /** A part: what went out now, per line (the command's `lines`). */
  handed?: { line_id: string; qty: string }[];
}

/** The server's final answer, as kept on the row. */
export interface HandoverAnswer {
  data?: Record<string, unknown>;
  flags: { code: string; message?: string; data?: Record<string, unknown> }[];
  reason?: string;
  message?: string;
  hold_id?: string;
  /** A manager's resolution of the hold (CR8B-4). */
  resolution?: HeldResolution;
}

/** A `handover_action` row as stored (JSON columns as text). */
export interface HandoverActionRow {
  id: string;
  order_id: string;
  outcome: 'all' | 'part';
  note: string | null;
  staff_id: string | null;
  at: string;
  day: string;
  detail: string | null;
  status: HandoverActionStatus;
  answer: string | null;
  answered_at: string | null;
}

/** This order is already handed over in full on this station (a double tap). */
export class OrderAlreadyHandedOverError extends Error {
  constructor(readonly orderId: string) {
    super(`This station already handed over order ${orderId}`);
    this.name = 'OrderAlreadyHandedOverError';
  }
}

/**
 * The full hand-over recorded here for `orderId`, if any — whatever the office answered: one
 * it didn't record still left the counter (D-076).
 */
export function findFullHandover(
  db: LocalReader,
  orderId: string,
): Promise<HandoverActionRow | null> {
  return db.getOptional<HandoverActionRow>(
    `SELECT * FROM handover_action
      WHERE order_id = ? AND outcome = 'all'
      ORDER BY at DESC LIMIT 1`,
    [orderId],
  );
}

/**
 * Records a hand-over, in the transaction of the command that sends it. Throws
 * {@link OrderAlreadyHandedOverError} (rolling the command back) when the order is already
 * handed over in full here (whatever the office answered).
 */
export async function recordHandoverAction(
  tx: LocalTx,
  row: {
    commandId: string;
    orderId: string;
    outcome: 'all' | 'part';
    note: string | null;
    staffId: string;
    at: string;
    day: string;
    detail: HandoverDetail;
  },
): Promise<void> {
  if (await findFullHandover(tx, row.orderId)) {
    throw new OrderAlreadyHandedOverError(row.orderId);
  }
  await tx.execute(
    `INSERT INTO handover_action (id, order_id, outcome, note, staff_id, at, day, detail, status, answer, answered_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'pending', NULL, NULL)`,
    [
      row.commandId,
      row.orderId,
      row.outcome,
      row.note,
      row.staffId,
      row.at,
      row.day,
      JSON.stringify(row.detail),
    ],
  );
}

/** A command's local effect that records the hand-over (for `StationData.enqueue`). */
export function handoverActionEffect(options: {
  orderId: string;
  outcome: 'all' | 'part';
  note: string | null;
  day: string;
  detail: HandoverDetail;
}): LocalEffect {
  return (tx, envelope) =>
    recordHandoverAction(tx, {
      commandId: envelope.command_id,
      orderId: options.orderId,
      outcome: options.outcome,
      note: options.note,
      staffId: envelope.staff_id,
      at: envelope.occurred_at,
      day: options.day,
      detail: options.detail,
    });
}

/**
 * Writes a final answer onto its hand-over (called by the uploader for every final answer; a
 * command that isn't a hand-over has no row and nothing changes). Transient answers never
 * reach here: the command stays `pending` and is sent again.
 */
export async function recordHandoverAnswer(
  tx: LocalTx,
  answer: CommandResponse,
  now: Date,
): Promise<void> {
  const id = answer.command_id;
  if (!id) return;
  if (
    answer.status !== 'applied' &&
    answer.status !== 'applied_with_flags' &&
    answer.status !== 'held' &&
    answer.status !== 'rejected'
  ) {
    return;
  }
  const kept: HandoverAnswer = {
    flags: 'flags' in answer && Array.isArray(answer.flags) ? answer.flags : [],
    ...('data' in answer && answer.data ? { data: answer.data } : {}),
    ...('reason' in answer && typeof answer.reason === 'string' ? { reason: answer.reason } : {}),
    ...('message' in answer && typeof answer.message === 'string'
      ? { message: answer.message }
      : {}),
    ...(answer.status === 'held' ? { hold_id: answer.hold_id } : {}),
  };
  await tx.execute(
    'UPDATE handover_action SET status = ?, answer = ?, answered_at = ? WHERE id = ?',
    [answer.status, JSON.stringify(kept), now.toISOString(), id],
  );
}

/**
 * Deletes answered hand-overs (applied, applied with flags, refused, and held ones a manager
 * has since booked or voided, CR8B-4) of days before `today`:
 * the customer's name stays on the station no longer than the day's orders do. Returns how
 * many went.
 */
export async function pruneHandoverActions(tx: LocalTx, today: string): Promise<number> {
  const old = await tx.getAll<{ id: string }>(
    `SELECT id FROM handover_action
      WHERE day < ? AND status IN ('applied', 'applied_with_flags', 'rejected', 'booked', 'voided')`,
    [today],
  );
  for (const r of old) await tx.execute('DELETE FROM handover_action WHERE id = ?', [r.id]);
  return old.length;
}

/**
 * Hand-overs to show: done on `day`, or still waiting for the server or kept for a manager
 * (any day). Oldest first.
 */
export function readHandoverActions(db: LocalReader, day: string): Promise<HandoverActionRow[]> {
  return db.getAll<HandoverActionRow>(
    `SELECT * FROM handover_action WHERE day = ? OR status IN ('pending', 'held')
      ORDER BY at, id`,
    [day],
  );
}
